// third party imports
import React, { useState, useEffect, useContext } from "react";

// internal rebet imports
import "./stylesWallet.css";
import Layout from "../../CommonComponents/Layout/Layout";

import { SIDER_TABS } from "../../../utils/Constants";
import EyeIcon from "../../../assets/icons/ic_eye_orange.png";

// component imports
import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../api/ApiRoutes";
import AppContext from "../../../context/AppContext";
import { Select, Table } from "antd";
import SaveButton from "../../CommonComponents/SaveButton/SaveButton";
import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import { useNavigate } from "react-router-dom";
import CustomAutoComplete from "../../CommonComponents/CustomAutoComplete/CustomAutoComplete";

const Wallet = () => {
  // non-state variables
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.IdToken
  );

  const { setActiveSidebarTab } = useContext(AppContext);
  const navigate = useNavigate();

  // state variables
  const [isApiRunning, setIsApiRunning] = useState(false);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [maxBalance, setMaxBalance] = useState(99999999);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 20,
    total_items: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });
  const [allOptions, setAllOptions] = useState([]);
  const [filteredWalletTransactions, setFilteredWalletTransactions] = useState([]);
  const [selectedOption, setSelectedOption] = useState("username");


  const columns = [
    {
      title: "User ID",
      dataIndex: "user_id",
      key: "user_id",
      width: 200,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      width: 150,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 250,
    },
    {
      title: "ReBet Cash Balance",
      dataIndex: "rebet_cash_balance",
      key: "rebet_cash_balance",
      sorter: (a, b) => a.rebet_cash_balance - b.rebet_cash_balance,
      render: (text, record) => {
        return (
          <div>
            <span>
              {Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(record.rebet_cash_balance)}
            </span>
          </div>
        );
      },
      width: 250,
    },
    {
      title: "Redeemable ReBet Cash Balance",
      dataIndex: "redeemable_cash_balance",
      key: "redeemable_cash_balance",
      sorter: (a, b) => a.redeemable_cash_balance - b.redeemable_cash_balance,
      width: 350,
      render: (text, record) => {
        return (
          <div>
            <span>
              {Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(record.redeemable_cash_balance)}
            </span>
          </div>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: 200,
      render: (text, record) => {
        return (
          <img
            className="wallet-transactions-table-actions-icon"
            src={EyeIcon}
            onClick={() => handleViewUserClicked(record)}
            alt="View User"
          />
        );
      },
    },
  ];

  const handleViewUserClicked = async (user_data) => {
    const userData = await getUserData(user_data.user_id);
    navigate(NAVIGATION_ROUTES?.USER_MANAGER, {
      state: {
        user_data: userData,
        wallet_active: false,
      },
    });
  };

  const getUserData = async (user_id) => {

    const params = {
      search_by: "user_id",
      search_str: user_id,
    };

    try {
      const response = await PrivateAdminPanelInstance?.post(
        API_ROUTES?.SEARCH_USER,
        params
      );
      const userData = response?.data?.data;
      return userData;
    } catch (error) {
      console.log("Error fetching user data", error);
    }
  };

  const getWalletTransactions = async ({ page = 1 }) => {
    setIsApiRunning(true);

    const params = {
      range_upper: maxBalance,
      page,
    };

    try {
      const response = await PrivateAdminPanelInstance?.post(
        API_ROUTES?.GET_WALLET_TRANSACTIONS,
        params
      );

      const walletTransactions = response?.data?.data?.data;
      const pagination = response?.data?.data?.pagination;

      console.log("walletTransactions", walletTransactions);
      if (page === 1) {
        setWalletTransactions(walletTransactions);
        setFilteredWalletTransactions(walletTransactions);
      } else {
        setWalletTransactions((prev) => [...prev, ...walletTransactions]);
        setFilteredWalletTransactions((prev) => [...prev, ...walletTransactions]);
      }
      setPagination(pagination);
    } catch (error) {
      console.log("Error fetching wallet transactions", error);
    } finally {
      setIsApiRunning(false);
    }
  };

  const loadMoreWalletTransactions = async () => {
    setIsApiRunning(true);
    getWalletTransactions({ page: pagination.current_page + 1 });
  };

  const handleChange = (value) => {
    const selectedPayment = allOptions.find((option) => option.value === value);
    setFilteredWalletTransactions(
      walletTransactions.filter(
        (payment) => payment[selectedOption] === selectedPayment.value
      )
    );
  };

  // use effects
  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.TRANSACTIONS, "Wallet");
  }, [setActiveSidebarTab]);

  useEffect(() => {
    getWalletTransactions({ page: 1 });
  }, []);

  useEffect(() => {
    if (selectedOption === "user_id") {
      setAllOptions(
        walletTransactions.map((payment) => ({
          label: payment.user_id,
          value: payment.user_id,
          user_id: payment.user_id,
        }))
      );
    } else if (selectedOption === "username") {
      setAllOptions(
        walletTransactions.map((payment) => ({
          label: payment.username,
          value: payment.username,
          user_id: payment.user_id,
        }))
      );
    } else {
      setAllOptions(
        walletTransactions.map((payment) => ({
          label: payment.email,
          value: payment.email,
          user_id: payment.user_id,
        }))
      );
    }
  }, [selectedOption, walletTransactions]);

  return (
    <Layout name={SIDER_TABS.TRANSACTIONS}>
      <div className="wallet-transactions-container">
        <h2>Wallet</h2>
        <div className="wallet-transactions-header">
          <div className="wallet-transactions-header-selects-container">
            <div className="wallet-transactions-header-selects-container-left">
              <div className="wallet-transactions-header-select">
                <span>Max Balance</span>
                <Select
                  value={maxBalance}
                  onChange={(value) => setMaxBalance(value)}
                >
                  <Select.Option value={99999999}>No Limit</Select.Option>
                  <Select.Option value={10000}>Up to $10k</Select.Option>
                  <Select.Option value={50000}>Up to $50k</Select.Option>
                  <Select.Option value={100000}>Up to $100k</Select.Option>
                  <Select.Option value={200000}>Up to $200k</Select.Option>
                  <Select.Option value={300000}>Up to $300k</Select.Option>
                  <Select.Option value={400000}>Up to $400k</Select.Option>
                  <Select.Option value={500000}>Up to $500k</Select.Option>
                </Select>
              </div>
            </div>
            <div className="manual-entries-header-action-buttons-container">
              <SaveButton
                isApiRunning={isApiRunning}
                callbackFunction={() => {
                  getWalletTransactions({ page: 1 });
                }}
                buttonText="Apply"
              />
            </div>
          </div>
          <div className="wallet-transactions-header-actions-container">
                <div className="wallet-transactions-header-select wallet-transactions-header-actions-search-by-select">
                  <span>Search by</span>
                  <Select
                    defaultValue={selectedOption}
                    onChange={(value) => setSelectedOption(value)}
                    options={[
                      { label: "Username", value: "username" },
                      { label: "User ID", value: "user_id" },
                      { label: "Email", value: "email" },
                    ]}
                  />
                </div>
                <div className="wallet-transactions-header-actions-container-search-bar">
                  <CustomAutoComplete
                    allOptions={allOptions}
                    handleChange={handleChange}
                    clearOptions={() => {
                      setFilteredWalletTransactions(walletTransactions);
                    }}
                    selector={"value"}
                    bgColor={"#23232B"}
                  />
                </div>
              </div>
        </div>

        <div className="wallet-transactions-body">
          <div className="wallet-transactions-table-container">
            <Table
              columns={columns}
              dataSource={filteredWalletTransactions}
              scroll={{ x: 1500, y: 400 }}
              pagination={false}
              rowKey={(record) => record.user_id + pagination.current_page}
            />

            {pagination.has_next && (
              <div className="wallet-transactions-table-load-more-button-container">
                <SaveButton
                  isApiRunning={isApiRunning}
                  buttonText="Load More"
                  callbackFunction={() => {
                    loadMoreWalletTransactions();
                  }}
                  isLoading={isApiRunning}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Wallet;
